/**
 * Enhances a complete customer quote with a compact visual preview.
 * The original richtext stays intact; without JavaScript the full quote displays.
 */
class UITestimonial extends HTMLElement {
  #quote = null;
  #toggle = null;
  #observer = null;
  #width = null;

  connectedCallback() {
    this.#quote = this.querySelector('[data-testimonial-quote]');
    this.#toggle = this.querySelector('[data-testimonial-toggle]');
    if (!this.#quote || !this.#toggle) return;

    this.#toggle.addEventListener('click', this.#toggleQuote);
    this.#observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width === this.#width) return;
      this.#width = width;
      this.#measure();
    });
    this.#observer.observe(this);
    this.#measure();
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#measure();
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#toggle?.removeEventListener('click', this.#toggleQuote);
    this.#width = null;
  }

  #measure() {
    const expanded = this.hasAttribute('data-expanded');
    this.setAttribute('data-preview', '');
    this.removeAttribute('data-expanded');

    // Read the actual clamped box after responsive sizing or a webfont change.
    const overflows = this.#quote.scrollHeight > this.#quote.clientHeight + 1;
    this.#toggle.hidden = !overflows;
    this.toggleAttribute('data-expanded', overflows && expanded);
    this.#syncLabel();
  }

  #toggleQuote = () => {
    this.toggleAttribute('data-expanded');
    this.#syncLabel();
  };

  #syncLabel() {
    const expanded = this.hasAttribute('data-expanded');
    const label = expanded ? 'Read less' : 'Read more';
    const attribution = this.dataset.attribution;
    this.#toggle.textContent = label;
    this.#toggle.setAttribute('aria-expanded', String(expanded));
    this.#toggle.setAttribute('aria-label', attribution ? `${label} from ${attribution}` : `${label} of this review`);
  }
}

if (!customElements.get('ui-testimonial')) {
  customElements.define('ui-testimonial', UITestimonial);
}
